import { HttpContext, HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, filter, Subject, switchMap, take, throwError } from 'rxjs';
import { setProps } from '@ngneat/elf';
import { AuthService } from './auth-service';
import { authStore } from './auth.store';
import { AppConfigService } from '../config/app-config.service';
import { isApiRequest } from '../interceptors/is-api-request';

/** Send a specific bearer token instead of the logged-in user's (e.g. public share links). */
export const PUBLIC_TOKEN = new HttpContextToken<string>(() => '');
export const publicTokenContext = (token: string) => new HttpContext().set(PUBLIC_TOKEN, token);

/** The bearer token and the logged-in user's id (`UserId`) for our API. */
function authHeaders(token: string | null): Record<string, string> {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const userId = authStore.query((state) => state.user?.id);
    if (userId !== undefined && userId !== null && userId !== '') headers['UserId'] = String(userId);
    return headers;
}

let refreshInProgress: Subject<boolean> | null = null;

const SKIP_REFRESH_URLS = [
    '/login',
    '/refreshtoken',
    '/forgotpassword',
    '/createpassword',
    '/resetpassword',
    '/send-reset-link',
    '/resend-reset-link',
];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    // Never send our token to third-party URLs
    if (!isApiRequest(req.url, inject(AppConfigService).apiOrigins())) {
        return next(req);
    }

    const authService = inject(AuthService);

    const publicToken = req.context.get(PUBLIC_TOKEN);
    if (publicToken) {
        return next(req.clone({ setHeaders: { Authorization: `Bearer ${publicToken}` } }));
    }

    const token = authStore.query((state) => state.token);

    const headers = authHeaders(token);
    const authReq = Object.keys(headers).length ? req.clone({ setHeaders: headers }) : req;

    return next(authReq).pipe(
        catchError((error) => {
            // Case-insensitive: the login endpoint is `/api/User/LoginWeb`.
            const isSkipped = SKIP_REFRESH_URLS.some((url) => req.url.toLowerCase().includes(url));
            if (isSkipped || error?.status !== 401) {
                return throwError(() => error);
            }

            const storedRefreshToken = authStore.query((state) => state.refreshToken);
            if (!storedRefreshToken) {
                authService.showSessionExpiredDialog();
                return throwError(() => error);
            }

            // If a refresh is already in progress, wait for it
            if (refreshInProgress) {
                return refreshInProgress.pipe(
                    filter((success) => success !== null),
                    take(1),
                    switchMap((success) => {
                        if (!success) {
                            return throwError(() => error);
                        }
                        const newToken = authStore.query((state) => state.token);
                        return next(
                            req.clone({
                                setHeaders: authHeaders(newToken),
                            })
                        );
                    })
                );
            }

            // Start a new refresh
            refreshInProgress = new Subject<boolean>();

            return authService.refreshToken(storedRefreshToken).pipe(
                switchMap((response) => {
                    const newAccessToken = response.data?.accessToken;
                    if (newAccessToken) {
                        authStore.update(
                            setProps({
                                token: newAccessToken,
                                // Keep the old refresh token if the backend doesn't rotate it
                                refreshToken: response.data.refreshToken ?? storedRefreshToken,
                            })
                        );
                        refreshInProgress!.next(true);
                        refreshInProgress!.complete();
                        refreshInProgress = null;

                        return next(
                            req.clone({
                                setHeaders: authHeaders(newAccessToken),
                            })
                        );
                    }

                    refreshInProgress!.next(false);
                    refreshInProgress!.complete();
                    refreshInProgress = null;
                    authService.showSessionExpiredDialog();
                    return throwError(() => error);
                }),
                catchError((refreshError) => {
                    refreshInProgress!.next(false);
                    refreshInProgress!.complete();
                    refreshInProgress = null;
                    if (refreshError?.status === 401) {
                        authService.logout();
                    } else {
                        authService.showSessionExpiredDialog();
                    }
                    return throwError(() => refreshError);
                })
            );
        })
    );
};
